import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ContactForm from './components/ContactForm';

const values = [
  { icon: 'ri-sparkling-2-fill', title: '清晰', desc: '把复杂的剧情梳理成容易对照的分集简介与故事线，拒绝故作高深。' },
  { icon: 'ri-heart-fill', title: '真诚', desc: '以剧情梳理与信息整理为本，不做标题党，也不刻意制造剧透焦虑。' },
  { icon: 'ri-scales-3-fill', title: '客观', desc: '简介力求还原故事本身走向，避免夹带主观偏见影响你的观看判断。' },
  { icon: 'ri-leaf-fill', title: '持续', desc: '持续扩充作品库与最新分集，让补番、追更、回顾都有一份可靠参考。' },
];

const principles = [
  '按话整理，逐话简介，信息结构一目了然',
  '剧情梳理 + 故事线解说，帮你快速把握主线',
  '明确标注幕次与关键转折，兼顾剧透友好',
  '内容以剧情交流与信息整理为目的，尊重原作',
];

const testimonials = [
  { name: '柚子', role: '学生党', text: '复习周没空追番，靠分集简介就能跟上剧情，特别适合我。' },
  { name: '江离', role: '老番回顾党', text: '想重温老番的时候，先看故事线梳理再决定补哪几集，效率翻倍。' },
  { name: '阿栎', role: '动画爱好者', text: '推荐的作品和分集整理都很用心，已经习惯先来这里看看再决定看什么。' },
];

const schema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于羞羞动漫网',
  description: '了解羞羞动漫网的品牌介绍、网站定位、发展理念、内容原则与联系方式。',
  inLanguage: 'zh-CN',
  mainEntityOfPage: 'https://laughland3.github.io/about',
};

export default function About() {
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <Navbar />

      {/* page header */}
      <div className="mx-auto max-w-7xl px-4 pt-20 md:px-6">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 pt-3 text-xs text-foreground-600">
          <Link to="/" className="inline-flex items-center gap-1 transition-colors hover:text-primary-600">
            <i className="ri-home-4-line" /> 首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="font-medium text-foreground-900">关于我们</span>
        </nav>
      </div>

      <main className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
        <div className="text-center">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-primary-600">
            <i className="ri-information-line" /> 关于我们
          </span>
          <h1 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-4xl">关于羞羞动漫网</h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-7 text-foreground-700 md:text-base">
            一个专注动漫分集剧情解说与每话简介的内容网站，让每一部番都能被看得更明白。
          </p>
        </div>

        {/* brand intro */}
        <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">品牌介绍</h2>
              <div className="mt-4 space-y-4 text-sm leading-7 text-foreground-700 md:text-base">
                <p>
                  羞羞动漫网诞生于一群动漫爱好者的共同困扰：想补一部老番，却不知道该从哪看起；追一部连载，忙到没空却担心错过剧情走向。
                  于是我们决定做一件事——把每一部番剧的每一话都整理成清晰的分集剧情简介，并配上剧情梳理与故事线解说。
                </p>
                <p>
                  如今，羞羞动漫网按话收录了多部作品的分集简介，覆盖奇幻冒险、治愈日常、武侠古风、科幻机战、青春校园、悬疑推理等热门类型。
                  无论你是补番党、追更党，还是只想回顾某段剧情的观众，都能在这里快速找到答案。
                </p>
              </div>
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">网站定位</h2>
              <p className="mt-3 text-sm leading-7 text-foreground-700 md:text-base">
                我们定位为「动漫剧情信息整理平台」：不生产盗版内容、不提供在线观看，只做每一话讲了什么的清晰记录与故事线梳理。
                让想了解剧情的人快速掌握走向，让决定是否追番的人先看清脉络，这是羞羞动漫网存在的意义。
              </p>
            </div>
          </div>

          {/* quick facts */}
          <div className="space-y-6">
            <div className="rounded-lg bg-foreground-950 p-6 md:p-8">
              <h2 className="font-heading text-lg font-bold text-background-50">我们相信</h2>
              <ul className="mt-4 space-y-3 text-sm text-background-200">
                <li className="flex items-start gap-2.5">
                  <i className="mt-0.5 ri-check-double-line text-accent-400" /> 好的剧情信息应该清晰、易读、好对照。
                </li>
                <li className="flex items-start gap-2.5">
                  <i className="mt-0.5 ri-check-double-line text-accent-400" /> 补番不该是负担，追更不该有断片焦虑。
                </li>
                <li className="flex items-start gap-2.5">
                  <i className="mt-0.5 ri-check-double-line text-accent-400" /> 内容真诚，尊重每一位观众与每一部作品。
                </li>
              </ul>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">发展理念</h2>
              <p className="mt-3 text-sm leading-7 text-foreground-700">
                从「帮你把番看懂」出发，用小而美的持续更新，陪伴每一位动漫爱好者。我们重视每一份留言与推荐，把它们视作网站成长的方向。
              </p>
            </div>
          </div>
        </section>

        {/* values */}
        <section className="mt-10">
          <h2 className="text-center font-heading text-xl font-bold text-foreground-950 md:text-2xl">我们的内容原则</h2>
          <p className="mx-auto mt-2 max-w-2xl text-center text-sm text-foreground-700">
            这些原则决定了羞羞动漫网每一篇分集剧情与剧情梳理的呈现方式。
          </p>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v) => (
              <div key={v.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-xl text-accent-700">
                  <i className={v.icon} />
                </span>
                <h3 className="mt-3 font-heading text-base font-bold text-foreground-900">{v.title}</h3>
                <p className="mt-1.5 text-sm leading-6 text-foreground-700">{v.desc}</p>
              </div>
            ))}
          </div>
          <ul className="mx-auto mt-6 grid max-w-3xl gap-3 sm:grid-cols-2">
            {principles.map((p) => (
              <li key={p} className="flex items-center gap-2.5 rounded-md bg-background-100 px-4 py-3 text-sm text-foreground-800">
                <i className="ri-arrow-right-s-fill text-primary-500" /> {p}
              </li>
            ))}
          </ul>
        </section>

        {/* testimonials */}
        <section className="mt-10">
          <h2 className="text-center font-heading text-xl font-bold text-foreground-950 md:text-2xl">大家的声音</h2>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
            {testimonials.map((t) => (
              <figure key={t.name} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <div className="flex items-center gap-1 text-accent-500">
                  <i className="ri-star-fill" />
                  <i className="ri-star-fill" />
                  <i className="ri-star-fill" />
                  <i className="ri-star-fill" />
                  <i className="ri-star-fill" />
                </div>
                <blockquote className="mt-3 flex-1 text-sm leading-7 text-foreground-700">“{t.text}”</blockquote>
                <figcaption className="mt-4 flex items-center gap-3 border-t border-background-200 pt-3">
                  <span className="text-sm font-semibold text-foreground-900">{t.name}</span>
                  <span className="text-xs text-foreground-600">· {t.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* contact */}
        <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">联系我们</h2>
            <p className="mt-3 text-sm leading-7 text-foreground-700">
              想推荐一部番剧、报告分集剧情勘误，或是提出任何建议？欢迎通过下面的表单留言，也可以直接发送邮件给我们。
            </p>
            <div className="mt-6 space-y-4">
              <a href="mailto:contact@xiuxiu-dongman.com" className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-xl text-primary-600">
                  <i className="ri-mail-fill" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-900">邮箱联系</p>
                  <p className="text-xs text-foreground-600">contact@xiuxiu-dongman.com</p>
                </div>
              </a>
              <Link to="/" className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-xl text-secondary-700">
                  <i className="ri-film-fill" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-900">浏览作品库</p>
                  <p className="text-xs text-foreground-600">返回首页，查看分集剧情与剧情梳理</p>
                </div>
              </Link>
            </div>
          </div>
          <ContactForm />
        </section>
      </main>

      <Footer />
    </>
  );
}